import clsx from 'clsx';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonWithPopoverAttached } from '@/core/ButtonWithPopoverAttached.molecule';
import { sqDurationStore, sqInvestigateStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { INVESTIGATE_TOOLS, TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { TRACK } from '@/track/track.constants';
import { doTrack } from '@/track/track.service';
import { Button } from '@seeqdev/qomponents';
import type { ButtonVariant } from '@seeqdev/qomponents';

interface CancelAndExecuteProps {
  /** the id of the tool currently open */
  toolId?: string;
  /** function to call when clicking the Cancel button (takes no parameters) */
  cancelAction: () => any;
  /** function to call when clicking the Execute button (takes no parameters) */
  submitAction: () => void | Promise<void>;
  /** true if the Execute button should be disabled */
  btnDisabled: boolean;
  /** the key of the currently active submit option */
  activeSubmitKey?: string | undefined;
  /** set the key of the currently active submit option */
  setActiveSubmitKey?: (value: string | undefined) => void;
  /** true to hide the cancel button */
  hideCancel?: boolean;
  hideExecute?: boolean;
  cancelBtnLabel?: string;
  /** the label for the button (Execute is used if nothing is specified) */
  submitBtnLabel?: string;
  /** an id for the Execute button */
  submitBtnId?: string;
  submitBtnTestId?: string;
  submitBtnVariant?: ButtonVariant;
  submitBtnFormattedLabel?: React.JSX.Element;
  submitBtnTooltip?: string;
  /** extra class names to be placed on the div around the buttons */
  extraClassNames?: string;
  submitOptions?: { key: string; label: string; variant?: ButtonVariant; submitFn: (...values: any[]) => any }[];
}

/**
 * Cancel and Execute buttons (used in tool panel)
 */
export const CancelAndExecute = ({
  toolId,
  cancelAction,
  submitAction,
  hideCancel,
  hideExecute,
  cancelBtnLabel = 'CANCEL',
  submitBtnLabel = 'EXECUTE',
  btnDisabled,
  activeSubmitKey,
  setActiveSubmitKey,
  submitBtnId,
  submitBtnTestId,
  submitBtnVariant = 'theme',
  submitBtnFormattedLabel,
  submitBtnTooltip,
  extraClassNames,
  submitOptions,
}: CancelAndExecuteProps) => {
  const [inProgress, setInProgress] = useState(false);
  const isTrackable = !_.isNil(toolId);
  const investigateRange = useFluxPath(sqDurationStore, () => sqDurationStore.investigateRange);
  const displayRange = useFluxPath(sqDurationStore, () => sqDurationStore.displayRange);
  // if the periodic condition is used in Organizer Topic for a date range configuration do not use trackAction
  const toolTrackAction =
    toolId === TREND_TOOLS.PERIODIC_CONDITION && submitBtnLabel === 'CONTINUE'
      ? 'Periodic Condition Continue Button'
      : _.get(_.find(INVESTIGATE_TOOLS, ['id', toolId]), 'trackAction');
  const activeSubmitOption = useMemo(
    () => submitOptions?.find((o) => o.key === activeSubmitKey) ?? submitOptions?.[0],
    [submitOptions, activeSubmitKey],
  );

  useEffect(() => {
    if (submitOptions && submitOptions.length > 0 && !activeSubmitKey) {
      setActiveSubmitKey?.(submitOptions[0].key);
    }
    if (!submitOptions || submitOptions.length === 0) {
      setActiveSubmitKey?.(undefined);
    }
  }, [submitOptions]);

  const trackAndCancel = () => {
    if (isTrackable) {
      doTrack(TRACK.WORKBENCH_TOOL, toolTrackAction as string, 'cancelled', { investigateRange, displayRange });
    }
    cancelAction();
  };

  const trackAndSubmit = () => {
    setInProgress(true);
    if (isTrackable) {
      doTrack(TRACK.WORKBENCH_TOOL, toolTrackAction as string, 'executed', { investigateRange, displayRange });
    }
    if (activeSubmitOption) {
      setActiveSubmitKey?.(activeSubmitOption.key);
    }
    const submitFunction = activeSubmitOption?.submitFn ?? submitAction;

    Promise.resolve(submitFunction())
      .then(() => setInProgress(sqInvestigateStore.activeTool !== toolId))
      .finally(() => setInProgress(false));
  };

  const { t } = useTranslation();
  return (
    <div className={clsx('tw:mt-sq-5 tw:flex tw:items-center tw:justify-center tw:gap-4', extraClassNames)}>
      <div className="tw:flex tw:flex-wrap tw:justify-center tw:gap-4">
        {!hideCancel && (
          <Button
            onClick={trackAndCancel}
            label={t(cancelBtnLabel)}
            size="sm"
            testId="cancelButton"
            extraClassNames="min-width-100 width-100 max-width-100"
          />
        )}

        {!hideExecute &&
          (submitOptions ? (
            activeSubmitOption && (
              <ButtonWithPopoverAttached
                id={activeSubmitOption.key}
                label={activeSubmitOption.label}
                disabled={inProgress || btnDisabled}
                testId={activeSubmitOption.key}
                onClick={trackAndSubmit}
                variant={activeSubmitOption?.variant ?? submitBtnVariant}
                inProgress={inProgress}
                showPopover={submitOptions.length > 0}
                popoverContent={
                  <div className="tw:flex tw:flex-col tw:gap-2">
                    {submitOptions.map((option) => (
                      <Button
                        key={option.label}
                        onClick={() => setActiveSubmitKey?.(option.key)}
                        label={t(option.label)}
                        size="sm"
                        variant="no-border"
                        extraClassNames="cursorPointer pl20 pr20 width-maximum fs14 tw:border-0  textAlignLeft noActiveBg tw:dark:text-sq-text-color-dark tw:dark:bg-sq-gray-highlight-dark"
                      />
                    ))}
                  </div>
                }
              />
            )
          ) : (
            <Button
              onClick={trackAndSubmit}
              disabled={inProgress || btnDisabled}
              size="sm"
              testId={submitBtnTestId ?? 'executeButton'}
              extraClassNames="min-width-100"
              id={submitBtnId}
              variant={submitBtnVariant}
              inProgress={inProgress}
              label={submitBtnFormattedLabel || t(submitBtnLabel)}
              tooltip={submitBtnTooltip}
            />
          ))}
      </div>
    </div>
  );
};
